import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import {
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useDeleteResource } from '../api/useMutations';
import ResourceGallery from './ResourceGallery';
import ResourceThumbnail from './ResourceThumbnail';
import type { GalleryItem } from './ResourceGallery';
import { ResourceSchemeContext, SharableLinkContext } from './ResourceSchemeContext';
import { sharableLinkDownloadUrl } from '../utils';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 140px)',
    gap: tokens.spacingHorizontalS,
  },
  clickable: {
    cursor: 'pointer',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'start' as const,
  },
  thumbnailWrapper: {
    position: 'relative',
    cursor: 'pointer',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'start' as const,
  },
  deleteOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(200, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '28px',
  },
  editHover: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: tokens.borderRadiusCircular,
    padding: '2px',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },
  thumbnailWrapperEditing: {
    ':hover > :last-child': {
      display: 'flex',
    },
  },
});

interface ResourcesTabProps {
  images: string[];
  videos: string[];
  domainId: number;
  callId: number;
  contentId: string | undefined;
  fileIdToResourceId: Record<string, number>;
  isEditing: boolean;
  saveSignal: number;
  resetSignal: number;
}

export default function ResourcesTab({
  images,
  videos,
  domainId,
  callId,
  contentId,
  fileIdToResourceId,
  isEditing,
  saveSignal,
  resetSignal,
}: ResourcesTabProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [pendingDeleteFileIds, setPendingDeleteFileIds] = useState<Set<string>>(new Set());

  const deleteResource = useDeleteResource(callId, contentId, domainId);
  const schemeMap = useContext(ResourceSchemeContext);
  const sharableLinkMap = useContext(SharableLinkContext);

  const galleryItems: GalleryItem[] = useMemo(
    () => [
      ...videos.map(id => ({ fileId: id, type: 'video' as const })),
      ...images.map(id => ({ fileId: id, type: 'image' as const })),
    ],
    [videos, images],
  );

  const prevSaveSignal = useRef(saveSignal);
  useEffect(() => {
    if (saveSignal !== prevSaveSignal.current) {
      prevSaveSignal.current = saveSignal;
      for (const fileId of pendingDeleteFileIds) {
        const resourceId = fileIdToResourceId[fileId];
        if (resourceId != null) {
          deleteResource.mutate({ resourceId, fileId });
        }
      }
      setPendingDeleteFileIds(new Set());
    }
  }, [saveSignal, pendingDeleteFileIds, fileIdToResourceId, deleteResource]);

  const prevResetSignal = useRef(resetSignal);
  useEffect(() => {
    if (resetSignal !== prevResetSignal.current) {
      prevResetSignal.current = resetSignal;
      setPendingDeleteFileIds(new Set());
    }
  }, [resetSignal]);

  const togglePendingDelete = (fileId: string) => {
    setPendingDeleteFileIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.grid}>
        {galleryItems.map((item, index) => (
          <button
            key={item.fileId}
            className={
              isEditing
                ? `${styles.thumbnailWrapper} ${styles.thumbnailWrapperEditing}`
                : styles.clickable
            }
            onClick={() => {
              if (isEditing) {
                togglePendingDelete(item.fileId);
                return;
              }
              const scheme = schemeMap[item.fileId] ?? 'xm';
              if (!scheme.startsWith('xm') && item.type === 'video' && sharableLinkMap[item.fileId]) {
                window.open(sharableLinkDownloadUrl(sharableLinkMap[item.fileId]), '_blank');
                return;
              }
              setSelectedIndex(index);
            }}
            aria-label={isEditing ? `${t('deleteResource')} ${index + 1}` : `${item.type === 'video' ? t('recordings') : t('images')} ${index + 1}`}
          >
            <ResourceThumbnail
              domainId={domainId}
              fileId={item.fileId}
              mediaType={item.type}
            />
            {isEditing && pendingDeleteFileIds.has(item.fileId) && (
              <div className={styles.deleteOverlay}>
                <DeleteRegular />
              </div>
            )}
            {isEditing && !pendingDeleteFileIds.has(item.fileId) && (
              <div className={styles.editHover}>
                <DeleteRegular />
              </div>
            )}
          </button>
        ))}
      </div>

      {selectedIndex !== null && !isEditing && (
        <ResourceGallery
          items={galleryItems}
          startIndex={selectedIndex}
          domainId={domainId}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </div>
  );
}
